import { useState, useRef, useEffect } from "react";
import { Play, Pause, Volume2, VolumeX, RefreshCcw, Edit2, Check, X, Loader2 } from "lucide-react";
import { BASE_URL } from "utils/constant";
import { IoIosRefresh } from "react-icons/io";

// Custom Audio Player Component
const CustomAudioPlayer = ({ mediaId, url, transcription, onClick }) => {
    // Keep track of the currently playing audio element across all instances
    // This module-scoped variable ensures only one <audio> plays at a time
    // eslint-disable-next-line no-redeclare
    /* global window */
    if (typeof window !== "undefined" && !window.__sm_currentlyPlayingAudio__) {
        window.__sm_currentlyPlayingAudio__ = null;
    }

    const [isPlaying, setIsPlaying] = useState(false);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [isMuted, setIsMuted] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editedTranscription, setEditedTranscription] = useState(transcription || "");
    const [isTriggering, setIsTriggering] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [localTranscription, setLocalTranscription] = useState(transcription);

    const audioRef = useRef(null);
    const progressBarRef = useRef(null);

    useEffect(() => {
        setLocalTranscription(transcription);
        setEditedTranscription(transcription || "");
    }, [transcription]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const setAudioData = () => {
            setDuration(audio.duration);
        };

        const setAudioTime = () => {
            setCurrentTime(audio.currentTime);
        };

        const handleEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
            if (typeof window !== "undefined" && window.__sm_currentlyPlayingAudio__ === audio) {
                window.__sm_currentlyPlayingAudio__ = null;
            }
        };

        const ensureSinglePlayback = () => {
            if (typeof window === "undefined") return;
            const current = window.__sm_currentlyPlayingAudio__;
            if (current && current !== audio) {
                try {
                    current.pause();
                } catch (_) {
                    // no-op: safe failure
                }
            }
            window.__sm_currentlyPlayingAudio__ = audio;
            setIsPlaying(true);
        };

        // Add event listeners
        audio.addEventListener('loadedmetadata', setAudioData);
        audio.addEventListener('timeupdate', setAudioTime);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('play', ensureSinglePlayback);

        return () => {
            audio.removeEventListener('loadedmetadata', setAudioData);
            audio.removeEventListener('timeupdate', setAudioTime);
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('play', ensureSinglePlayback);
            if (typeof window !== "undefined" && window.__sm_currentlyPlayingAudio__ === audio) {
                // Clear the global pointer if this instance is unmounting
                window.__sm_currentlyPlayingAudio__ = null;
            }
        };
    }, []);

    const togglePlay = (e) => {
        e.stopPropagation();
        const audio = audioRef.current;
        if (!audio) return;

        if (audio.paused) {
            // Ensure any other audio stops before playing this one
            if (typeof window !== "undefined") {
                const current = window.__sm_currentlyPlayingAudio__;
                if (current && current !== audio) {
                    try {
                        current.pause();
                    } catch (_) {
                        // no-op
                    }
                }
                window.__sm_currentlyPlayingAudio__ = audio;
            }
            const playPromise = audio.play();
            if (playPromise && typeof playPromise.then === "function") {
                playPromise.then(() => setIsPlaying(true)).catch(() => {
                    // Autoplay might be blocked; keep UI consistent
                    setIsPlaying(false);
                });
            } else {
                setIsPlaying(true);
            }
        } else {
            audio.pause();
            setIsPlaying(false);
            if (typeof window !== "undefined" && window.__sm_currentlyPlayingAudio__ === audio) {
                window.__sm_currentlyPlayingAudio__ = null;
            }
        }
    };

    const toggleMute = (e) => {
        e.stopPropagation();
        audioRef.current.muted = !isMuted;
        setIsMuted(!isMuted);
    };

    const handleProgressChange = (e) => {
        e.stopPropagation();
        const time = Number(e.target.value);
        audioRef.current.currentTime = time;
        setCurrentTime(time);
    };

    const formatTime = (time) => {
        if (isNaN(time)) return "0:00";
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const handleTriggerTranscription = async (e) => {
        e.stopPropagation();
        if (isTriggering || !mediaId) return;
        setIsTriggering(true);
        try {
            const accessToken = localStorage.getItem("access");
            const response = await fetch(`${BASE_URL}/core/snag-object/trigger-transcription/${mediaId}/`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            if (response.ok) {
                // Transcription triggered successfully
            }
        } catch (error) {
            console.error("Error triggering transcription:", error);
        } finally {
            setIsTriggering(false);
        }
    };

    const handleUpdateTranscription = async (e) => {
        e.stopPropagation();
        if (isUpdating || !mediaId) return;
        setIsUpdating(true);
        try {
            const accessToken = localStorage.getItem("access");
            const response = await fetch(`${BASE_URL}/core/snag-object/update-transcription/${mediaId}/`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({ transcription: editedTranscription }),
            });
            if (response.ok) {
                setLocalTranscription(editedTranscription);
                setIsEditing(false);
            }
        } catch (error) {
            console.error("Error updating transcription:", error);
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <div className="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200 w-full max-w-[280px]" onClick={onClick}>
            <audio ref={audioRef} src={url} preload="metadata" />

            <div className="flex flex-row items-end gap-3">
                <button
                    onClick={togglePlay}
                    className="p-2 rounded-full bg-purple-600 text-white hover:bg-purple-700 transition-colors flex-shrink-0"
                >
                    {isPlaying ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" className="" />}
                </button>

                <div className="flex flex-col gap-1 flex-grow">
                    <input
                        type="range"
                        min={0}
                        max={duration || 0}
                        value={currentTime}
                        onChange={handleProgressChange}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                    />
                    <div className="flex justify-between text-[10px] text-gray-500 font-medium">
                        <span>{formatTime(currentTime)}</span>
                        <span>{formatTime(duration)}</span>
                    </div>
                </div>

                <button
                    onClick={toggleMute}
                    className="p-1.5 text-gray-500 hover:bg-gray-200 rounded-full transition-colors"
                >
                    {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                </button>
            </div>

            {(localTranscription || isEditing) && (
                <div className="mt-1 px-1 group flex flex-col gap-1">
                    <div className="flex items-center justify-between">
                        <span className="text-[9px] text-gray-400 font-semibold uppercase tracking-wider">Transcription</span>
                        {!isEditing && (
                            <div className=" items-center gap-1 hidden group-hover:flex transition-opacity">
                                <button
                                    onClick={handleTriggerTranscription}
                                    disabled={isTriggering}
                                    title="Re-transcribe"
                                    className="p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                                >
                                    {isTriggering ? <Loader2 size={12} className="animate-spin" /> : <IoIosRefresh size={12} color="black" />}
                                </button>
                                <button
                                    onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}
                                    title="Edit transcription"
                                    className="p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                                >
                                    <Edit2 size={12} />
                                </button>
                            </div>
                        )}
                    </div>

                    {isEditing ? (
                        <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
                            <textarea
                                value={editedTranscription}
                                onChange={(e) => setEditedTranscription(e.target.value)}
                                className="text-[11px] text-gray-700 bg-white border border-purple-200 rounded p-1.5 focus:outline-none focus:ring-1 focus:ring-purple-400 min-h-[60px] resize-none"
                                placeholder="Enter transcription..."
                                autoFocus
                            />
                            <div className="flex justify-end gap-1">
                                <button
                                    onClick={() => { setIsEditing(false); setEditedTranscription(localTranscription); }}
                                    className="p-1 text-gray-500 hover:bg-gray-100 rounded transition-colors"
                                    disabled={isUpdating}
                                >
                                    <X size={14} />
                                </button>
                                <button
                                    onClick={handleUpdateTranscription}
                                    disabled={isUpdating || !editedTranscription.trim()}
                                    className="p-1 text-purple-600 hover:bg-purple-50 rounded transition-colors"
                                >
                                    {isUpdating ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <p className="text-[11px] text-gray-600 italic leading-relaxed border-l-2 border-purple-300 pl-2">
                            "{localTranscription}"
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default CustomAudioPlayer;